import {z} from "zod";
import {mongoObjectIdSchema} from "./mongo.ts";

/**
 * Document types the print-profiles wire contract currently produces/accepts
 * (print-profiles.md §1-2, `loadPrintProfiles`/`savePrintProfile`). The entity
 * doc (profil-pryntera.md, `documentType`) lists a broader illustrative set
 * ("PROTOCOL, NONCONFORMITY_ACT, EKMT тощо"); only these two are implemented.
 */
export const PrintProfileDocumentTypes = ["PROTOCOL", "ELECTRONIC_DOCUMENT"] as const;
export const printProfileDocumentTypeSchema = z.enum(PrintProfileDocumentTypes);
export type PrintProfileDocumentType = z.infer<typeof printProfileDocumentTypeSchema>;

export const PrintProfileBlankVersions = [1, 2] as const;
export const printProfileBlankVersionSchema = z.literal(PrintProfileBlankVersions);
export type PrintProfileBlankVersion = z.infer<typeof printProfileBlankVersionSchema>;

/** Normalized (`0..1`) position/size of one printed field on the A4 page. print-profiles.md §5. */
export const printProfileFieldLayoutSchema = z.object({
    id: z.string().trim().min(1),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().min(0).max(1),
    height: z.number().min(0).max(1),
    /** Defaults to `true` when absent; hides the field's `FieldChip` when `false`. */
    enabled: z.boolean().optional(),
});
export type PrintProfileFieldLayout = z.infer<typeof printProfileFieldLayoutSchema>;

/**
 * Shared offset applied to every field, in millimeters, each within −25…+25.
 * Step is 1mm (`offsetStepMm`); profil-pryntera.md's attribute table still
 * says 2,5 — print-profiles.md §2 (audited 30.08.2026) corrects that against
 * the prototype's actual slider step.
 */
export const printProfileFieldOffsetsSchema = z.object({
    x: z.number().min(-25).max(25),
    y: z.number().min(-25).max(25),
});
export type PrintProfileFieldOffsets = z.infer<typeof printProfileFieldOffsetsSchema>;

/** `GET /api/otk/print-profiles` row. print-profiles.md §1. */
export const otkPrintProfileSchema = z.object({
    id: mongoObjectIdSchema,
    name: z.string().trim().min(1).max(160),
    documentType: printProfileDocumentTypeSchema,
    blankVersion: printProfileBlankVersionSchema,
    fieldLayouts: z.array(printProfileFieldLayoutSchema),
    fieldOffsets: printProfileFieldOffsetsSchema,
    /** Shared scale of the field group about the page center, within `0.8…1.2`; `1` is default. */
    fieldScale: z.number().min(0).max(10),
    /** Prototype-only reset marker for the hard-coded `hp` reference layout; not defined by any norm or architecture doc. */
    layoutVersion: z.number().int().nonnegative().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export type OtkPrintProfile = z.infer<typeof otkPrintProfileSchema>;

/**
 * `POST /api/otk/print-profiles/:id?` body. The server generates the id when
 * the path parameter is absent and always computes the timestamps.
 */
export const otkPrintProfileInputSchema = otkPrintProfileSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    layoutVersion: true,
});
export type OtkPrintProfileInput = z.infer<typeof otkPrintProfileInputSchema>;
